
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { CircleNotch, FileText, Lightning, MagnifyingGlass, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { Chip, ImmoButton, LeerZustand, SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { Karte, WahlFeld } from './bereich/Formular';
import EmailVorlageFormular from './EmailVorlageFormular';
import { VORLAGEN_TRIGGER, triggerLabel } from '../../../lib/emails/vorlagenTrigger';

interface EmailVorlage {
  id: string;
  name: string;
  kategorie: string;
  beschreibung: string | null;
  betreff_template: string;
  html_template: string;
  ki_trigger?: string | null;
  aktiv?: boolean;
  updated_at: string;
  created_at: string;
}

const KATEGORIE_CONFIG: Record<string, { label: string }> = {
  mieter: { label: 'Mieter' },
  eigentuemer: { label: 'Eigentümer' },
  dienstleister: { label: 'Dienstleister' },
  allgemein: { label: 'Allgemein' },
  zahlungserinnerung: { label: 'Zahlungserinnerung' },
  vertrag: { label: 'Vertrag' },
  wartung: { label: 'Wartung' },
};

const KATEGORIE_CHIPS = ['alle', 'mieter', 'eigentuemer', 'dienstleister', 'allgemein', 'zahlungserinnerung', 'vertrag', 'wartung'];

const getKatConfig = (k: string) => KATEGORIE_CONFIG[k] || KATEGORIE_CONFIG['allgemein'];

interface EmailVorlagenGridProps {
  userProfile?: any;
}

const EmailVorlagenGrid: React.FC<EmailVorlagenGridProps> = ({ userProfile }) => {
  const [vorlagen, setVorlagen] = useState<EmailVorlage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeKategorie, setActiveKategorie] = useState('alle');
  const [showFormular, setShowFormular] = useState(false);
  const [editVorlage, setEditVorlage] = useState<EmailVorlage | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { mobil } = useMobilModus();
  // Fehler als Toast statt alert() – gleiche Texte (Entscheidung 8).
  const [toast, zeigeToast] = useToastPill();

  useEffect(() => {
    fetchVorlagen();
  }, []);

  const fetchVorlagen = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('email_vorlagen')
        .select('*')
        .order('updated_at', { ascending: false });
      setVorlagen(data || []);
    } catch (err) {
      console.error('Error fetching email vorlagen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('email_vorlagen').delete().eq('id', id);
      if (error) throw error;
      setConfirmDeleteId(null);
      fetchVorlagen();
    } catch (err: any) {
      zeigeToast('Fehler beim Löschen: ' + err.message, true);
    }
  };

  const handleSaved = () => {
    setShowFormular(false);
    setEditVorlage(null);
    fetchVorlagen();
  };

  // Trigger-Verwaltung: eine Vorlage pro automatischer Aktion — Zuordnung wechseln
  // löst die bisherige und setzt die neue (Eindeutigkeit erzwungen).
  const [triggerBusy, setTriggerBusy] = useState<string | null>(null);
  const handleTriggerZuordnung = async (triggerKey: string, vorlageId: string) => {
    setTriggerBusy(triggerKey);
    try {
      const { error: clearErr } = await supabase.from('email_vorlagen').update({ ki_trigger: null }).eq('ki_trigger', triggerKey);
      if (clearErr) throw clearErr;
      if (vorlageId) {
        const { error: setErr } = await supabase.from('email_vorlagen').update({ ki_trigger: triggerKey }).eq('id', vorlageId);
        if (setErr) throw setErr;
      }
      await fetchVorlagen();
    } catch (err: any) {
      zeigeToast('Zuordnung fehlgeschlagen: ' + err.message, true);
    } finally {
      setTriggerBusy(null);
    }
  };

  const kategorieCounts = vorlagen.reduce<Record<string, number>>((acc, v) => {
    acc[v.kategorie] = (acc[v.kategorie] || 0) + 1;
    return acc;
  }, {});

  const filtered = vorlagen.filter(v => {
    const matchesSearch = !searchTerm || v.name.toLowerCase().includes(searchTerm.toLowerCase())
      || v.kategorie.toLowerCase().includes(searchTerm.toLowerCase())
      || v.beschreibung?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesKategorie = activeKategorie === 'alle' || v.kategorie === activeKategorie;
    return matchesSearch && matchesKategorie;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '80px 0', gap: 12 }}>
        <CircleNotch size={26} className="animate-spin" color="var(--ink3)" />
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Lade E-Mail-Vorlagen...</p>
      </div>
    );
  }

  const neu = () => { setEditVorlage(null); setShowFormular(true); };
  const h = mobil ? 44 : 38;

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      {/* Suche + Neue Vorlage */}
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <label className="immo-glas flex items-center" style={{ flex: '1 1 220px', minWidth: 0, height: 44, borderRadius: 22, gap: 8, padding: '0 14px' }}>
          <MagnifyingGlass size={17} color="var(--ink3)" aria-hidden />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Vorlage suchen…" aria-label="Vorlage suchen"
            className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
        </label>
        <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={neu}>Neue Vorlage</ImmoButton>
      </div>

      {/* Kategorien */}
      <div role="tablist" aria-label="Kategorien" className="flex flex-wrap self-start" style={{ padding: 3, borderRadius: 15, background: 'var(--fill2)', maxWidth: '100%' }}>
        {KATEGORIE_CHIPS.map(k => {
          const aktiv = activeKategorie === k;
          return (
            <button key={k} type="button" role="tab" aria-selected={aktiv} onClick={() => setActiveKategorie(k)} className="flex items-center cursor-pointer"
              style={{ height: mobil ? 44 : 32, padding: '0 12px', gap: 6, borderRadius: 12, border: 'none', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                background: aktiv ? 'var(--acc-fill)' : 'transparent', color: aktiv ? 'var(--acc-ink)' : 'var(--ink2)' }}>
              {k === 'alle' ? 'Alle' : getKatConfig(k).label}
              <span className="immo-zahl" style={{ opacity: 0.6 }}>{k === 'alle' ? vorlagen.length : (kategorieCounts[k] || 0)}</span>
            </button>
          );
        })}
      </div>

      {/* Karten */}
      {filtered.length === 0 ? (
        <LeerZustand icon={FileText}
          titel={searchTerm || activeKategorie !== 'alle' ? 'Keine passende Vorlage' : 'Noch keine Vorlagen'}
          text={searchTerm || activeKategorie !== 'alle' ? 'Andere Suche oder Kategorie versuchen.' : 'Lege deine erste E-Mail-Vorlage an.'}>
          {!searchTerm && activeKategorie === 'alle' && (
            <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={neu} style={{ marginTop: 6 }}>Neue Vorlage</ImmoButton>
          )}
        </LeerZustand>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 330px), 1fr))', gap: 12 }}>
          {filtered.map(v => (
            <div key={v.id} role="button" tabIndex={0}
              onClick={() => { setEditVorlage(v); setShowFormular(true); }}
              onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) { setEditVorlage(v); setShowFormular(true); } }}
              className="immo-glas immo-hover-kachel flex flex-col cursor-pointer min-w-0" style={{ borderRadius: 22, padding: 16, gap: 8 }}>
              <div className="flex items-start" style={{ gap: 8 }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--acc-soft)' }}>
                  <FileText size={18} weight="fill" color="var(--acc)" />
                </span>
                <div className="flex flex-col flex-1 min-w-0">
                  <span lang="de" style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3, overflowWrap: 'break-word', hyphens: 'auto' }}>{v.name}</span>
                  {v.beschreibung && <span className="line-clamp-2" style={{ fontSize: 12, color: 'var(--ink3)' }}>{v.beschreibung}</span>}
                </div>
                <div className="flex items-center shrink-0" onClick={e => e.stopPropagation()}>
                  {confirmDeleteId === v.id ? (
                    <div className="flex items-center" style={{ gap: 4 }}>
                      <button type="button" onClick={() => handleDelete(v.id)} className="cursor-pointer"
                        style={{ height: mobil ? 44 : 32, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--bad)', color: '#fff', fontSize: 12, fontWeight: 800 }}>Löschen</button>
                      <button type="button" onClick={() => setConfirmDeleteId(null)} className="cursor-pointer"
                        style={{ height: mobil ? 44 : 32, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 700 }}>Abbrechen</button>
                    </div>
                  ) : (
                    <>
                      <button type="button" onClick={() => { setEditVorlage(v); setShowFormular(true); }} title="Bearbeiten" aria-label="Bearbeiten"
                        className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ width: h, height: h, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink2)' }}>
                        <PencilSimple size={18} />
                      </button>
                      <button type="button" onClick={() => setConfirmDeleteId(v.id)} title="Löschen" aria-label="Löschen"
                        className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ width: h, height: h, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--bad)' }}>
                        <Trash size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--ink2)', overflowWrap: 'anywhere' }}><span style={{ color: 'var(--ink3)' }}>Betreff</span> {v.betreff_template || '—'}</span>
              <div className="flex flex-wrap items-center" style={{ gap: 5, marginTop: 'auto' }}>
                <Chip groesse="xs">{getKatConfig(v.kategorie).label}</Chip>
                {v.ki_trigger && (
                  <span title={`Auto: ${triggerLabel(v.ki_trigger)}`} className="inline-flex items-center min-w-0" style={{ maxWidth: '100%', height: 20, padding: '0 8px', gap: 4, borderRadius: 10, fontSize: 10, fontWeight: 800, background: 'var(--warn-soft)', color: 'var(--warn)' }}>
                    <Lightning size={11} weight="fill" className="shrink-0" /><span className="truncate">Auto: {triggerLabel(v.ki_trigger)}</span>
                  </span>
                )}
                <span className="immo-zahl" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink3)' }}>
                  {new Date(v.updated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Automatische Aktionen — welche Vorlage bei welcher Aktion */}
      <Karte titel={<span className="flex items-center" style={{ gap: 8 }}><Lightning size={17} weight="fill" color="var(--warn)" />Automatische Aktionen</span>}>
        {VORLAGEN_TRIGGER.map((t) => {
          const zugeordnet = vorlagen.find((v) => v.ki_trigger === t.key);
          return (
            <div key={t.key} className="flex flex-wrap items-center" style={{ gap: 12, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
              <span className="flex flex-col min-w-0" style={{ flex: '1 1 220px', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{t.label}</span>
                <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.45 }}>{t.beschreibung}</span>
              </span>
              <span style={{ flex: '1 1 200px', minWidth: 0 }}>
                <WahlFeld wert={zugeordnet?.id ?? ''} disabled={triggerBusy === t.key} onChange={(id) => handleTriggerZuordnung(t.key, id)} ariaLabel={t.label}>
                  <option value="">— keine Vorlage —</option>
                  {vorlagen.filter((v) => v.aktiv !== false).map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </WahlFeld>
              </span>
            </div>
          );
        })}
      </Karte>

      {showFormular && (
        <EmailVorlageFormular
          vorlage={editVorlage}
          mobil={mobil}
          onClose={() => { setShowFormular(false); setEditVorlage(null); }}
          onSaved={handleSaved}
        />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
};

export default EmailVorlagenGrid;
